/* Valèur theme script: menu drawer, product gallery, video play and pause, add to bag. No dependencies. */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Menu drawer: sit the panel under the header, lock page scroll while open, close on Escape or a link tap. */
  const drawer = document.querySelector('[data-menu-drawer]');
  const header = document.querySelector('[data-header]');
  if (drawer && header) {
    drawer.addEventListener('toggle', () => {
      if (drawer.open) {
        const bottom = Math.round(header.getBoundingClientRect().bottom);
        document.documentElement.style.setProperty('--header-bottom', `${bottom}px`);
      }
      document.documentElement.classList.toggle('is-locked', drawer.open);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.open) {
        drawer.open = false;
        drawer.querySelector('summary').focus();
      }
    });
    drawer.addEventListener('click', (event) => {
      if (event.target.closest('a')) drawer.open = false;
    });
    window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => {
      if (event.matches) drawer.open = false;
    });
  }

  /* Product gallery below 990px: dots follow the swipe, arrows step one photo, one read per frame. */
  document.querySelectorAll('[data-gallery]').forEach((track) => {
    const gallery = track.parentElement;
    const dots = gallery.querySelectorAll('.product__dot');
    const prev = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    const count = track.children.length;
    let current = -1;
    const update = () => {
      const index = Math.min(count - 1, Math.max(0, Math.round(track.scrollLeft / Math.max(track.clientWidth, 1))));
      if (index === current) return;
      current = index;
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      if (prev) prev.hidden = index === 0;
      if (next) next.hidden = index === count - 1;
    };
    let queued = false;
    track.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        update();
        queued = false;
      });
    }, { passive: true });
    const step = (direction) => track.scrollBy({ left: direction * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    if (prev) prev.addEventListener('click', () => step(-1));
    if (next) next.addEventListener('click', () => step(1));
    update();
  });

  /* Back link over the gallery: go back if the visitor came from this site, otherwise follow the link to the shop. */
  document.querySelectorAll('[data-back]').forEach((link) => {
    link.addEventListener('click', (event) => {
      if (document.referrer.startsWith(window.location.origin) && window.history.length > 1) {
        event.preventDefault();
        window.history.back();
      }
    });
  });

  /* Videos: load and play only while on screen and the tab is visible, and never play for visitors who ask for
     reduced motion. Browsers pause video in background tabs, so a returning visitor's on-screen video is restarted. */
  const videos = document.querySelectorAll('video[autoplay], video[data-autoplay]');
  if (reduceMotion) {
    videos.forEach((video) => {
      video.pause();
      video.removeAttribute('autoplay');
      if (!video.closest('.hero, .story')) video.controls = true; /* hero and story loops are scenery: keep the still, no player bar */
    });
  } else if ('IntersectionObserver' in window) {
    const onScreen = new Set();
    const play = (video) => {
      const playing = video.play();
      if (playing) playing.catch(() => {});
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          onScreen.add(entry.target);
          if (!document.hidden) play(entry.target);
        } else {
          onScreen.delete(entry.target);
          entry.target.pause();
        }
      });
    }, { threshold: 0.15 });
    videos.forEach((video) => observer.observe(video));
    document.addEventListener('visibilitychange', () => {
      onScreen.forEach((video) => (document.hidden ? video.pause() : play(video)));
    });
  }

  /* Sideways rails (video reviews): the arrows step a screenful and hide at either end. One read per frame. */
  document.querySelectorAll('[data-rail]').forEach((rail) => {
    const track = rail.querySelector('[data-rail-track]');
    const prev = rail.querySelector('[data-rail-prev]');
    const next = rail.querySelector('[data-rail-next]');
    if (!track) return;
    const update = () => {
      const end = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.hidden = track.scrollLeft <= 2;
      if (next) next.hidden = track.scrollLeft >= end;
    };
    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        update();
        queued = false;
      });
    };
    track.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    const step = (direction) => track.scrollBy({ left: direction * track.clientWidth * 0.9, behavior: reduceMotion ? 'auto' : 'smooth' });
    if (prev) prev.addEventListener('click', () => step(-1));
    if (next) next.addEventListener('click', () => step(1));
    update();
  });

  /* Review films: a tap turns this film's sound on and every other film's off. */
  const soundButtons = document.querySelectorAll('[data-reel-sound]');
  soundButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const video = button.parentElement.querySelector('video');
      if (!video) return;
      const turnOn = video.muted;
      soundButtons.forEach((other) => {
        const otherVideo = other.parentElement.querySelector('video');
        if (otherVideo) otherVideo.muted = true;
        other.setAttribute('aria-pressed', 'false');
      });
      if (turnOn) {
        video.muted = false;
        button.setAttribute('aria-pressed', 'true');
        const playing = video.play();
        if (playing) playing.catch(() => {});
      }
    });
  });

  /* Welcome offer: opens once after a delay, or at once to show the code (or an error) after a sign-up.
     Closing it any way remembers the visitor for the number of days set on the section. */
  const offer = document.querySelector('[data-offer]');
  if (offer && typeof offer.showModal === 'function') {
    const key = 'valeur-offer';
    const days = Number(offer.dataset.days || 30);
    const seen = () => {
      try {
        const at = Number(window.localStorage.getItem(key) || 0);
        return at > 0 && Date.now() - at < days * 86400000;
      } catch (storageError) {
        return false;
      }
    };
    const remember = () => {
      try {
        window.localStorage.setItem(key, String(Date.now()));
      } catch (storageError) {
        /* Private browsing: the offer may be shown again on a later visit. */
      }
    };
    let opener = null;
    const open = () => {
      if (offer.open) return;
      opener = document.activeElement;
      offer.showModal();
      document.documentElement.classList.add('is-locked');
    };
    const close = () => {
      if (offer.open) offer.close();
    };
    offer.addEventListener('close', () => {
      remember();
      document.documentElement.classList.remove('is-locked');
      if (opener && typeof opener.focus === 'function') opener.focus();
    });
    offer.addEventListener('click', (event) => {
      if (event.target.closest('[data-offer-close]')) {
        close();
        return;
      }
      if (event.target !== offer) return;
      const box = offer.getBoundingClientRect();
      const outside = event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
      if (outside) close();
    });
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', (event) => { if (event.target.contains(offer)) open(); });
      document.addEventListener('shopify:section:deselect', (event) => { if (event.target.contains(offer)) close(); });
    } else if (offer.querySelector('[data-offer-success], [data-offer-error]')) {
      open();
    } else if (!seen()) {
      window.setTimeout(open, Number(offer.dataset.delay || 6) * 1000);
    }
  }

  /* Stock stamp: lands once, the first time it is well into view. Reduced motion: it is simply there. */
  const stamps = document.querySelectorAll('[data-stamp]');
  if (reduceMotion || !('IntersectionObserver' in window)) {
    stamps.forEach((stamp) => stamp.classList.add('is-stamped'));
  } else if (stamps.length) {
    const stampObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-stamped');
        stampObserver.unobserve(entry.target);
      });
    }, { threshold: 0.6 });
    stamps.forEach((stamp) => stampObserver.observe(stamp));
  }

  /* Add to bag without leaving the page. On a network failure the form posts normally. */
  const toast = document.querySelector('[data-toast]');
  let toastTimer;
  const showToast = () => {
    if (!toast) return;
    clearTimeout(toastTimer);
    toast.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => toast.classList.add('is-visible')));
    toastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
      toastTimer = setTimeout(() => { toast.hidden = true; }, 350);
    }, 3200);
  };
  const setCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  };

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-form-error]');
    form.addEventListener('submit', async (event) => {
      if (!window.fetch) return;
      event.preventDefault();
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
      if (button) button.setAttribute('aria-busy', 'true');
      let response;
      let data;
      try {
        response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form),
        });
        data = await response.json();
      } catch (networkError) {
        form.submit();
        return;
      } finally {
        if (button) button.removeAttribute('aria-busy');
      }
      if (!response.ok) {
        const message = data.description || data.message || 'This piece could not be added to your bag.';
        if (error) {
          error.textContent = message;
          error.hidden = false;
        }
        return;
      }
      try {
        const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        setCount(cart.item_count);
      } catch (countError) {
        /* The item is in the bag; only the header count is stale until the next page load. */
      }
      showToast();
    });
  });

  /* Try it on: the swipe position drives everything, one read per frame. The model's photos cross-fade, each
     floating bag slides and scales between its side and the model's chest, and each colour's details fade in
     place. Reduced motion: the photos cross-fade and the floating bags stay hidden. */
  const initTryOn = (section) => {
    const stage = section.querySelector('.tryon__stage');
    const track = section.querySelector('[data-tryon-track]');
    const model = section.querySelector('.tryon__model');
    if (!stage || !track || !model) return;
    const looks = model.querySelectorAll('.tryon__look');
    const ghosts = stage.querySelectorAll('.tryon__ghost');
    const infos = section.querySelectorAll('.tryon__info');
    const prev = section.querySelector('[data-tryon-prev]');
    const next = section.querySelector('[data-tryon-next]');
    const indexLabel = section.querySelector('[data-tryon-index]');
    const fill = section.querySelector('[data-tryon-fill]');
    const count = track.children.length;
    const [landX, landY, landW] = (section.dataset.land || '54.9,55.5,32.3').split(',').map((n) => Number(n) / 100);
    let wide = false;
    let side = 0;
    let current = -1;

    /* Each floating bag sits exactly on the worn one at rest: same centre, and twice its width (the body fills
       half of each bag image). The swipe then moves it sideways from there. */
    const place = () => {
      const s = stage.getBoundingClientRect();
      const m = model.getBoundingClientRect();
      wide = s.width >= 990;
      const bag = m.width * landW;
      const x = m.left - s.left + m.width * landX;
      const y = m.top - s.top + m.height * landY;
      ghosts.forEach((ghost) => {
        ghost.style.width = `${bag * 2}px`;
        ghost.style.left = `${x - bag}px`;
        ghost.style.top = `${y - bag * 0.95}px`; /* the images are 960 x 912 */
      });
      side = wide ? Math.max(s.width * 0.22, m.width / 2 + bag * 0.75 + 20) : s.width * 0.42;
    };

    const render = () => {
      const p = Math.min(count - 1, Math.max(0, track.scrollLeft / Math.max(track.clientWidth, 1)));
      const grow = wide ? 0.5 : 0.3;
      looks.forEach((look, k) => {
        look.style.opacity = k === 0 ? 1 : Math.min(1, Math.max(0, p - k + 1));
      });
      ghosts.forEach((ghost, k) => {
        const d = k - p;
        const t = Math.min(1, Math.abs(d));
        const fade = Math.max(0, 1 - Math.max(0, Math.abs(d) - 1) * 2.5);
        ghost.style.opacity = reduceMotion ? 0 : (0.32 * t * fade).toFixed(3);
        ghost.style.transform = `translate3d(${(d * side).toFixed(1)}px, 0, 0) scale(${(1 + grow * t).toFixed(3)})`;
      });
      infos.forEach((info, k) => {
        const shown = Math.min(1, Math.max(0, 1 - Math.abs(k - p) * 2.5));
        info.style.opacity = shown;
        info.style.visibility = shown > 0.01 ? 'visible' : 'hidden';
      });
      if (fill) fill.style.transform = `scaleX(${((p + 1) / count).toFixed(4)})`;
      const index = Math.round(p);
      if (index !== current) {
        current = index;
        if (indexLabel) indexLabel.textContent = String(index + 1).padStart(2, '0');
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === count - 1;
      }
    };

    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        render();
      });
    };
    track.addEventListener('scroll', schedule, { passive: true });
    const go = (index) => track.scrollTo({ left: index * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    if (prev) prev.addEventListener('click', () => go(Math.max(0, current - 1)));
    if (next) next.addEventListener('click', () => go(Math.min(count - 1, current + 1)));
    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      go(Math.min(count - 1, Math.max(0, current + (event.key === 'ArrowRight' ? 1 : -1))));
    });
    const refit = () => {
      place();
      render();
    };
    if ('ResizeObserver' in window) new ResizeObserver(refit).observe(stage);
    else window.addEventListener('resize', refit, { passive: true });
    refit();
  };
  document.querySelectorAll('[data-tryon]').forEach(initTryOn);

  /* The discount code on the product page: tapping it copies the code, and it reads "Copied" for two seconds. */
  document.querySelectorAll('[data-copy-code]').forEach((pill) => {
    const code = pill.dataset.copyCode;
    const label = pill.querySelector('[data-copy-label]');
    const status = pill.parentElement.querySelector('[data-copy-status]');
    let timer;
    const copy = async () => {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
        return;
      }
      const field = document.createElement('textarea');
      field.value = code;
      field.setAttribute('readonly', '');
      field.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
      document.body.appendChild(field);
      field.select();
      const ok = document.execCommand('copy');
      field.remove();
      if (!ok) throw new Error('copy failed');
    };
    pill.addEventListener('click', async () => {
      try {
        await copy();
      } catch (error) {
        return; // the code stays on the pill to type by hand
      }
      label.textContent = pill.dataset.copied || 'Copied';
      if (status) status.textContent = pill.dataset.copiedStatus || '';
      clearTimeout(timer);
      timer = setTimeout(() => {
        label.textContent = code;
        if (status) status.textContent = '';
      }, 2000);
    });
  });
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-tryon]').forEach(initTryOn));
    document.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('.tryon__slide');
      if (slide) slide.parentElement.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
    });
  }

  /* The hero's review stars: the average of the written reviews on this page, so they can only ever show what is
     really there. Hidden when the page has no rated review. Tapping them scrolls to the reviews. */
  document.querySelectorAll('[data-hero-rating]').forEach((pill) => {
    const ratings = [...document.querySelectorAll('.quote .stars')]
      .map((el) => parseFloat(el.style.getPropertyValue('--rating')))
      .filter((r) => r > 0);
    const reviews = document.querySelector('.quotes[id]');
    if (!ratings.length || !reviews) return;
    const average = Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10;
    pill.querySelectorAll('.hero-rating__stars i').forEach((star, i) => {
      star.style.setProperty('--fill', Math.round(Math.max(0, Math.min(1, average - i)) * 100) / 100);
    });
    pill.setAttribute('aria-label', (pill.dataset.label || '').replace('[rating]', average.toFixed(1)));
    pill.href = `#${reviews.id}`;
    pill.addEventListener('click', (event) => {
      event.preventDefault();
      reviews.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    });
    pill.hidden = false;
  });

  /* In the round: scroll position picks the frame of the turn. The canvas draws the frame, the studio wall past
     it (each frame's edges are a still model of the wall, so they stretch cleanly), and the colour name multiplied
     into the wall and cut away wherever the frame's matte says the bag is, so the bag stands in front of it.
     Frames load as the section approaches, coarse to fine; nothing runs while it is off screen. With reduced
     motion the stage is not pinned and shows one still. */
  document.querySelectorAll('[data-round]').forEach((section) => {
    const stage = section.querySelector('.round__stage');
    const canvas = section.querySelector('.round__canvas');
    const ctx = canvas.getContext('2d');
    const data = JSON.parse(section.querySelector('[data-round-data]').textContent);
    const [FW, FH] = data.size;
    const [MW, MH] = data.matte;
    const counts = data.frames.map((list) => list.length);
    const T = data.timeline;
    const frames = data.frames.map((urls) => urls.map(() => null));
    const mattes = [null, null];
    const notes = [...section.querySelectorAll('[data-note]')].map((el) => ({
      el, range: el.dataset.note.split(',').map(Number), text: el.textContent.trim(),
    }));
    const caption = section.querySelector('[data-round-caption]');
    const captionIndex = section.querySelector('[data-round-index]');
    const captionText = section.querySelector('[data-round-text]');
    const fill = section.querySelector('[data-round-fill]');
    const angle = section.querySelector('[data-round-angle]');
    const looks = section.querySelectorAll('.round__look');
    const link = section.querySelector('[data-round-link]');
    const family = getComputedStyle(document.documentElement).getPropertyValue('--serif').trim() || 'serif';
    const layer = document.createElement('canvas');
    const lctx = layer.getContext('2d');
    const backs = [document.createElement('canvas'), document.createElement('canvas')];
    const mix = document.createElement('canvas');
    const mctx = mix.getContext('2d');
    const glyphs = [document.createElement('canvas'), document.createElement('canvas')];
    const seg = (p, a, b) => Math.min(1, Math.max(0, (p - a) / (b - a)));
    const ease = (t) => t * t * (3 - 2 * t);
    let L = null;
    let fontReady = false;
    let near = false;
    let started = false;
    let shown = -1;
    let raf = 0;
    let last = 0;
    const xs = [-1, -1];   // the frame each colour shows, as a fraction between two neighbouring frames
    let state = '';
    let warmed = false;

    const paintBack = (c) => {
      const img = frames[c][0];
      if (!L || !img) return;
      const { W, H, fx, fy, fw, fh } = L;
      const b = backs[c];
      b.width = W;
      b.height = H;
      const g = b.getContext('2d');
      // the still wall and floor: each half carries its edge column across, the strip above carries the top row up
      // the still wall and floor: each half carries its edge column across, the top and bottom rows carry on past
      // the frame (the frame's top and edges are already this still wall, so nothing shows where they meet)
      const top = fy + fh * 0.06;
      const floor = fy + fh * 0.97;
      g.drawImage(img, 1, 0, 1, FH, 0, fy, W / 2 + 1, fh);
      g.drawImage(img, FW - 2, 0, 1, FH, W / 2, fy, W / 2, fh);
      if (fy > 0) {
        g.drawImage(img, 1, 1, 1, 1, 0, 0, Math.max(fx, 0) + 1, fy + 1);
        g.drawImage(img, FW - 2, 1, 1, 1, fx + fw - 1, 0, Math.max(W - fx - fw, 0) + 1, fy + 1);
      }
      g.drawImage(img, 0, 1, FW, 1, fx, 0, fw, top);
      g.drawImage(img, 0, FH - 2, FW, 1, fx, floor, fw, H - floor);
      b.ready = true;
    };

    const layout = () => {
      const w = stage.clientWidth;
      const h = stage.clientHeight;
      if (!w || !h) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(5e6 / (w * h)));
      const W = Math.round(w * dpr);
      const H = Math.round(h * dpr);
      canvas.width = W;
      canvas.height = H;
      mix.width = W;
      mix.height = H;
      ctx.imageSmoothingQuality = 'high';
      mctx.imageSmoothingQuality = 'high';
      // the frame stands on the stage floor with wall above it for the word; phones keep the word above the bag
      const wide = w / h > 0.68;
      const fh = (wide ? 0.88 : 0.84) * H;
      const fw = (fh * FW) / FH;
      const fx = (W - fw) / 2;
      const fy = H - fh;
      // the word: as large as the width and the room above the bag allow. On wide screens the flap covers its foot;
      // on phones, where the bag is as wide as the word, it stands on the top of the strap instead
      lctx.font = `400 100px ${family}`;
      const m = data.words.map((word) => lctx.measureText(word));
      const cap = lctx.measureText('N').actualBoundingBoxAscent / 100;
      const rise = Math.max(...m.map((x) => x.actualBoundingBoxAscent)) / 100;
      const fall = Math.max(...m.map((x) => x.actualBoundingBoxDescent)) / 100;
      const widest = Math.max(...m.map((x) => x.width)) / 100;
      const overlap = wide ? 0.36 : 0.3;
      const anchor = fy + (wide ? 0.225 : 0.108) * fh;
      const room = (wide ? 78 : 84) * dpr;
      const size = Math.min((W * 0.9) / widest, (anchor - room) / (rise - overlap * cap));
      const base = anchor + overlap * cap * size;
      glyphs.forEach((g, i) => {
        g.width = Math.ceil((m[i].width / 100) * size + 8 * dpr);
        g.height = Math.ceil((rise + fall) * size + 8 * dpr);
        const gc = g.getContext('2d');
        gc.font = `400 ${size}px ${family}`;
        gc.fillStyle = data.ink;
        gc.fillText(data.words[i], 4 * dpr, rise * size + 4 * dpr);
      });
      layer.width = W;
      layer.height = Math.ceil(Math.min(H, base + fall * size + 8 * dpr));
      L = { W, H, fx, fy, fw, fh, dpr, top: base - rise * size - 4 * dpr };
      L.stick = parseFloat(getComputedStyle(stage).top) || 0;
      L.span = Math.max(1, section.offsetHeight - stage.offsetHeight);
      stage.style.setProperty('--fx', `${fx / dpr}px`);
      stage.style.setProperty('--fy', `${fy / dpr}px`);
      stage.style.setProperty('--fw', `${fw / dpr}px`);
      stage.style.setProperty('--fh', `${fh / dpr}px`);
      backs.forEach((b, c) => { b.ready = false; paintBack(c); });
      state = '';
      warmed = false;
    };

    // the first use of a picture uploads it to the graphics card, and the mattes are large: do that for the mattes
    // and the colour change's canvases while the page is idle, not in the middle of a scroll
    const warm = () => {
      if (warmed || !near || !L || !backs[0].ready || !backs[1].ready || !mattes[0] || !mattes[1]) return;
      warmed = true;
      (window.requestIdleCallback || setTimeout)(() => {
        mattes.forEach((m) => lctx.drawImage(m, 0, 0, 1, 1, 0, 0, 1, 1));
        mctx.drawImage(backs[1], 0, 0);
        ctx.globalAlpha = 0.004;
        ctx.drawImage(mix, 0, 0);
        ctx.drawImage(backs[1], 0, 0);
        ctx.globalAlpha = 1;
        state = '';
        request();
        settle();
      });
    };

    // then hand each frame to the graphics card once, a few at a time while the page is idle, so the first pass
    // through the turn never waits on one
    let settled = 0;
    const settle = () => {
      const idle = window.requestIdleCallback || ((fn) => setTimeout(() => fn({ timeRemaining: () => 8 }), 50));
      idle((deadline) => {
        const all = [...frames[0], ...frames[1]];
        while (settled < all.length && all[settled] && deadline.timeRemaining() > 2) {
          lctx.drawImage(all[settled], 0, 0, 1, 1, 0, 0, 1, 1);
          settled += 1;
        }
        if (settled < all.length) settle();
      });
    };

    // where along its frames a colour is, from how far through its turn the scroll is: the frames are evenly
    // spaced in how much the bag changes (data.pos), so a steady scroll turns the bag at a steady pace
    const along = (c, u) => {
      const pos = data.pos[c];
      let k = 0;
      while (k < pos.length - 2 && pos[k + 1] <= u) k += 1;
      return k + Math.min(1, Math.max(0, (u - pos[k]) / (pos[k + 1] - pos[k])));
    };
    // the two frames either side of that place and how far between them, or just the nearest loaded frame
    const pair = (c) => {
      const x = Math.max(0, Math.min(counts[c] - 1, xs[c]));
      const k = Math.floor(x);
      const f = x - k;
      const a = pick(c, k);
      const b = f > 0.004 && k + 1 < counts[c] && frames[c][k + 1] && a === k ? k + 1 : -1;
      return { a, b, f: b >= 0 ? f : 0 };
    };

    const pick = (c, i) => {
      const list = frames[c];
      for (let d = 0; d < counts[c]; d++) {
        if (list[i - d]) return i - d;
        if (list[i + d]) return i + d;
      }
      return -1;
    };

    // where a frame is drawn: in place, or scaled about the frame centre and shifted (in film pixels) so the two
    // front views line up while the colour changes
    const place = (s, dx, dy) => {
      const k = L.fw / FW;
      return [L.fx + ((FW / 2) * (1 - s) + dx) * k, L.fy + ((FH / 2) * (1 - s) + dy) * k, L.fw * s, L.fh * s];
    };
    const cut = (c, i, r) => {
      const row = Math.floor(i / data.cols);
      const col = i % data.cols;
      lctx.drawImage(mattes[c], col * MW, row * MH, MW, MH, r[0], r[1], r[2], (r[3] * data.band) / FH);
    };

    const draw = (p) => {
      if (!L) return;
      const { W } = L;
      // the Noir turns, holds at three-quarters, changes into the Etoupe there, and the Etoupe turns on to face front
      const t = ease(seg(p, ...T.fade));
      const out = ease(seg(p, ...T.out));
      const into = ease(seg(p, ...T.into));
      const { s, dx, dy } = data.swap;
      const ra = t > 0 ? place(s ** -t, (-dx * t) / s, (-dy * t) / s) : place(1, 0, 0);
      const rb = place(s ** (1 - t), dx * (1 - t), dy * (1 - t));
      const A = t < 1 ? pair(0) : { a: -1 };
      const B = t > 0 ? pair(1) : { a: -1 };
      const ia = A.a;
      const ib = B.a;
      // a frame, blended into the next one by how far between them the turn is
      const frame = (g, c, P, r) => {
        g.drawImage(frames[c][P.a], ...r);
        if (P.b >= 0) {
          g.globalAlpha *= P.f;
          g.drawImage(frames[c][P.b], ...r);
        }
      };
      const wordOn = fontReady && mattes[0] && mattes[1];
      const wall = (b, g) => {
        if (b.ready) g.drawImage(b, 0, 0);
        else { g.fillStyle = data.wall; g.fillRect(0, 0, L.W, L.H); }
      };
      const key = `${ia}|${A.b}|${(A.f || 0).toFixed(3)}|${ib}|${B.b}|${(B.f || 0).toFixed(3)}|${t.toFixed(3)}|${wordOn ? p.toFixed(4) : ''}`;
      if (key !== state && (ia >= 0 || ib >= 0)) {
        state = key;
        if (ia >= 0) {
          ctx.globalAlpha = 1;
          wall(backs[0], ctx);
          frame(ctx, 0, A, ra);
          ctx.globalAlpha = 1;
        }
        if (ib >= 0 && ia < 0) {
          wall(backs[1], ctx);
          frame(ctx, 1, B, rb);
          ctx.globalAlpha = 1;
        } else if (ib >= 0) {
          // the Etoupe's wall and frame together at the fade's opacity, so its wall never shows as a panel
          wall(backs[1], mctx);
          frame(mctx, 1, B, rb);
          mctx.globalAlpha = 1;
          ctx.globalAlpha = t;
          ctx.drawImage(mix, 0, 0);
        }
        if (wordOn) {
          // each name drifts slowly across its half of the scroll; at the change the Noir slides off and fades,
          // then the Etoupe slides in
          const drift = W * 0.06;
          lctx.globalCompositeOperation = 'source-over';
          lctx.clearRect(0, 0, layer.width, layer.height);
          if (out < 1) {
            lctx.globalAlpha = 1 - out;
            lctx.drawImage(glyphs[0], (W - glyphs[0].width) / 2 + drift * (0.5 - seg(p, 0, T.out[1]) - out * 0.6), L.top);
          }
          if (into > 0) {
            lctx.globalAlpha = into;
            lctx.drawImage(glyphs[1], (W - glyphs[1].width) / 2 + drift * (0.5 - seg(p, T.into[0], 1) + (1 - into) * 0.6), L.top);
          }
          lctx.globalAlpha = 1;
          lctx.globalCompositeOperation = 'destination-out';
          if (ia >= 0) cut(0, ia, ra);
          if (A.b >= 0) cut(0, A.b, ra);
          if (ib >= 0) cut(1, ib, rb);
          if (B.b >= 0) cut(1, B.b, rb);
          ctx.globalCompositeOperation = 'multiply';
          ctx.globalAlpha = data.strength;
          ctx.drawImage(layer, 0, 0);
          ctx.globalCompositeOperation = 'source-over';
        }
        ctx.globalAlpha = 1;
      }
      if (fill) fill.style.transform = `scaleX(${p.toFixed(4)})`;
      if (angle) {
        const c = p < T.look ? 0 : 1;
        const turn = data.angles[c][Math.max(0, Math.min(counts[c] - 1, Math.round(xs[c])))];
        angle.textContent = `${String(turn).padStart(3, '0')}°`;
      }
      let active = -1;
      notes.forEach((note, i) => {
        const on = p >= note.range[0] && p <= note.range[1];
        note.el.classList.toggle('is-on', on);
        if (on) active = i;
      });
      if (caption) {
        if (active >= 0 && caption.dataset.note !== String(active)) {
          caption.dataset.note = String(active);
          captionIndex.textContent = String(active + 1).padStart(2, '0');
          captionText.textContent = notes[active].text;
        }
        caption.classList.toggle('is-on', active >= 0);
      }
      const look = p < T.look ? 0 : 1;
      looks.forEach((el, i) => el.classList.toggle('is-on', i === look));
      if (link && link.dataset.look !== String(look)) {
        link.dataset.look = String(look);
        link.href = data.looks[look].url;
        link.textContent = data.looks[look].label;
      }
    };

    const progress = () => (reduceMotion ? 0 : Math.min(1, Math.max(0, (L.stick - section.getBoundingClientRect().top) / L.span)));
    const tick = (now) => {
      raf = 0;
      if (!L) return;
      const target = progress();
      const dt = last ? Math.min(64, now - last) : 16.7;
      last = now;
      shown = shown < 0 ? target : shown + (target - shown) * (1 - Math.pow(0.86, dt / 16.7));
      if (Math.abs(target - shown) < 0.0003) shown = target;
      // while the page moves, show exactly where the turn is (blending two frames); once it stops, settle on
      // the nearest whole frame so a still page is always sharp
      let settling = false;
      [T.a, T.b].forEach((range, c) => {
        // eased in and out of the holds, so the turn never starts or stops with a jolt
        const x = along(c, ease(seg(shown, ...range)));
        const goal = shown !== target ? x : Math.round(x);
        xs[c] = xs[c] < 0 ? goal : xs[c] + (goal - xs[c]) * (1 - Math.pow(0.7, dt / 16.7));
        if (Math.abs(goal - xs[c]) < 0.004) xs[c] = goal;
        if (xs[c] !== goal) settling = true;
      });
      draw(shown);
      if (shown !== target || settling) raf = requestAnimationFrame(tick);
      else last = 0;
    };
    const request = () => { if (!raf && (near || shown < 0)) raf = requestAnimationFrame(tick); };

    const load = () => {
      if (started) return;
      started = true;
      const queue = [];
      const seen = new Set();
      [12, 6, 3, 1].forEach((step) => [0, 1].forEach((c) => {
        for (let i = 0; i < counts[c]; i += step) {
          if (!seen.has(`${c}-${i}`)) { seen.add(`${c}-${i}`); queue.push([c, i]); }
        }
      }));
      const get = (url, done) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => (img.decode ? img.decode() : Promise.resolve()).catch(() => {}).then(() => done(img));
        img.src = url;
      };
      data.mattes.forEach((url, c) => get(url, (img) => { mattes[c] = img; state = ''; request(); warm(); }));
      let next = 0;
      const pump = () => {
        if (next >= queue.length) return;
        const [c, i] = queue[next++];
        get(data.frames[c][i], (img) => {
          frames[c][i] = img;
          if (i === 0) paintBack(c);
          state = '';
          request();
          warm();
          pump();
        });
      };
      for (let k = 0; k < 6; k++) pump();
    };

    new IntersectionObserver(([entry]) => { if (entry.isIntersecting) load(); }, { rootMargin: '150% 0px' }).observe(section);
    new IntersectionObserver(([entry]) => { near = entry.isIntersecting; request(); warm(); }, { rootMargin: '25% 0px' }).observe(section);
    window.addEventListener('scroll', request, { passive: true });
    new ResizeObserver(() => { layout(); request(); warm(); }).observe(stage);
    (document.fonts ? document.fonts.load(`400 100px ${family}`) : Promise.resolve()).catch(() => {}).then(() => {
      fontReady = true;
      layout();
      request();
    });
  });

  /* Worn close: as the section scrolls past, each layer moves in step with the scroll at its own rate (the
     Étoupe slowest, the Noir fastest), so the Noir floats up through the lettering while the lettering stands up
     from a tilt. Only scrolling moves anything, and only by transforms, so it runs on the compositor. With
     reduced motion everything stays put. */
  document.querySelectorAll('[data-float]').forEach((section) => {
    if (reduceMotion) return;
    const type = section.querySelector('.float__type');
    const near = section.querySelector('.float__near');
    const far = section.querySelector('.float__far');
    const narrow = window.matchMedia('(max-width: 749px)');
    const seg = (p, a, b) => Math.min(1, Math.max(0, (p - a) / (b - a)));
    const ease = (t) => t * t * (3 - 2 * t);
    const mix = (a, b, t) => a + (b - a) * t;
    let raf = 0;
    let shown = -1;
    let last = 0;
    let on = false;
    let pass = 1;
    let vh = window.innerHeight / 100;
    const size = () => {
      vh = window.innerHeight / 100;
      pass = section.offsetHeight + window.innerHeight;
    };
    const draw = (p) => {
      // p runs from 0 as the section's top meets the bottom of the screen to 1 as its bottom leaves the top, and
      // every move is a straight line in p, so each layer keeps a steady speed against the page: the Noir runs
      // ahead of the page, the lettering lags it a little, the Étoupe lags it most. On phones the bag is as wide
      // as the lettering, so it travels less.
      const d = p - 0.5;
      const stand = ease(seg(p, 0.08, 0.48));
      const [tk, nk, fk] = narrow.matches ? [24, 34, 50] : [36, 52, 70];
      type.style.transform = `translate3d(0, ${(d * tk * vh).toFixed(1)}px, 0) rotateX(${((1 - stand) * 26).toFixed(2)}deg) scale(${mix(1.06, 1, stand).toFixed(4)})`;
      near.style.transform = `translate3d(0, ${(-d * nk * vh).toFixed(1)}px, 0) rotate(${(d * -4).toFixed(2)}deg) scale(${mix(1.06, 0.98, p).toFixed(4)})`;
      far.style.transform = `translate3d(0, ${(d * fk * vh).toFixed(1)}px, 0) rotate(${mix(7, -5, p).toFixed(2)}deg)`;
    };
    const progress = () => Math.min(1, Math.max(0, (window.innerHeight - section.getBoundingClientRect().top) / pass));
    const tick = (now) => {
      raf = 0;
      const target = progress();
      const dt = last ? Math.min(64, now - last) : 16.7;
      last = now;
      shown = shown < 0 ? target : shown + (target - shown) * (1 - Math.pow(0.86, dt / 16.7));
      if (Math.abs(target - shown) < 0.0003) shown = target;
      draw(shown);
      if (shown !== target) raf = requestAnimationFrame(tick);
      else last = 0;
    };
    const request = () => { if (!raf && (on || shown < 0)) raf = requestAnimationFrame(tick); };
    new IntersectionObserver(([entry]) => { on = entry.isIntersecting; request(); }, { rootMargin: '25% 0px' }).observe(section);
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', () => { size(); request(); });
    size();
    request();
  });

  /* Our story: as the section scrolls past, the scene draws back a little, the word stands up from a tilt and the
     card rises in, in step with the scroll. Transforms only. With reduced motion everything stays put. */
  document.querySelectorAll('[data-house]').forEach((section) => {
    if (reduceMotion) return;
    const scene = section.querySelector('[data-house-scene]');
    const word = section.querySelector('[data-house-word]');
    const card = section.querySelector('[data-house-card]');
    if (!scene || !card) return;
    const seg = (p, a, b) => Math.min(1, Math.max(0, (p - a) / (b - a)));
    const ease = (t) => t * t * (3 - 2 * t);
    const mix = (a, b, t) => a + (b - a) * t;
    let raf = 0;
    let shown = -1;
    let last = 0;
    let on = false;
    const draw = (p) => {
      const arrive = ease(seg(p, 0, 0.55));
      scene.style.transform = `scale(${mix(1.1, 1, arrive).toFixed(4)})`;
      const stand = ease(seg(p, 0.1, 0.5));
      if (word) word.style.transform = `translate3d(0, ${mix(6, -2, p).toFixed(2)}vh, 0) rotateX(${((1 - stand) * 28).toFixed(2)}deg)`;
      const rise = ease(seg(p, 0.25, 0.55));
      card.style.transform = `translate3d(0, ${((1 - rise) * 48).toFixed(1)}px, 0)`;
      card.style.opacity = (0.2 + rise * 0.8).toFixed(3);
    };
    const progress = () => {
      const r = section.getBoundingClientRect();
      return Math.min(1, Math.max(0, (window.innerHeight - r.top) / (r.height + window.innerHeight)));
    };
    const tick = (now) => {
      raf = 0;
      const target = progress();
      const dt = last ? Math.min(64, now - last) : 16.7;
      last = now;
      shown = shown < 0 ? target : shown + (target - shown) * (1 - Math.pow(0.86, dt / 16.7));
      if (Math.abs(target - shown) < 0.0003) shown = target;
      draw(shown);
      if (shown !== target) raf = requestAnimationFrame(tick);
      else last = 0;
    };
    const request = () => { if (!raf && (on || shown < 0)) raf = requestAnimationFrame(tick); };
    new IntersectionObserver(([entry]) => { on = entry.isIntersecting; request(); }, { rootMargin: '25% 0px' }).observe(section);
    window.addEventListener('scroll', request, { passive: true });
    request();
  });
})();
